// Central data adapter to shield UI from path/schema changes
// New preferred schema (company-centric):
// - users/{userId}
// - companies/{companyId} (root doc contains details, plan, features)
// - companies/{companyId}/{collectionName}
// Legacy schema (user-centric):
// - artifacts/{appId}/users/{userId}/companyDetails/myCompany
// - artifacts/{appId}/users/{userId}/Settings/default
// - artifacts/{appId}/users/{userId}/{collectionName}

import { doc, getDoc, getDocs, collection, setDoc, updateDoc } from 'firebase/firestore';

// Normalize collection names (avoid spaces/casing issues)
function normalizeCollectionName(name) {
  if (!name) return name;
  const map = {
    Salesbills: 'salesBills',
    salesbills: 'salesBills',
    salesBills: 'salesBills',
    Challan: 'challan',
    challan: 'challan',
    challans: 'challan',
    Quotation: 'quotation',
    quotation: 'quotation',
    quotations: 'quotation',
    'purchase bills': 'purchaseBills',
    purchasebills: 'purchaseBills',
    purchaseBills: 'purchaseBills',
    'purchase orders': 'purchaseOrders',
    purchaseorders: 'purchaseOrders',
    purchaseOrders: 'purchaseOrders',
    irregularpayments: 'irregularPayments',
    irregularPayments: 'irregularPayments',
    salarypayment: 'salaryPayments',
    salaryPayment: 'salaryPayments',
    salaryPayments: 'salaryPayments',
    Settings: 'settings',
    settings: 'settings',
    Device: 'devices',
    device: 'devices',
    banner: 'banners',
    // Todos normalization
    todos: 'todolist',
    todoList: 'todolist',
    todolist: 'todolist',
    // Keep employees and EmployeeSalarydetails as distinct collections
    employees: 'employees',
    EmployeeSalarydetails: 'EmployeeSalarydetails',
    templateConfigs: 'templateConfigs',
    // Returns and notes collections
    salesReturns: 'salesReturns',
    salesreturns: 'salesReturns',
    purchaseReturns: 'purchaseReturns',
    purchasereturns: 'purchaseReturns',
    creditNotes: 'creditNotes',
    creditnotes: 'creditNotes',
    debitNotes: 'debitNotes',
    debitnotes: 'debitNotes',
    // Fixed assets, investments, loans
    fixedAssets: 'fixedAssets',
    fixedassets: 'fixedAssets',
    investments: 'investments',
    loans: 'loans',
    // Employee cash transactions
    employeeCashTransactions: 'employeeCashTransactions',
    employeecashtransactions: 'employeeCashTransactions',
    workOrders: 'workOrders',
    workordercostledger: 'workOrderCostLedger',
    workOrderCostLedger: 'workOrderCostLedger',
  };
  return map[name] || name;
}

export function getWorkOrderPhasesRef(db, companyId, workOrderId) {
  if (!db || !companyId || !workOrderId) return null;
  return collection(db, 'companies', companyId, 'workOrders', workOrderId, 'phases');
}

export function getWorkOrderDocRef(db, companyId, workOrderId) {
  if (!db || !companyId || !workOrderId) return null;
  return doc(db, 'companies', companyId, 'workOrders', workOrderId);
}

// Resolve new company root doc
export function getCompanyDocRef(db, companyId) {
  if (!db || !companyId) return null;
  return doc(db, 'companies', companyId);
}

// Resolve new company subcollection ref
export function getCompanyCollectionRef(db, companyId, collectionName) {
  if (!db || !companyId || !collectionName) return null;
  const norm = normalizeCollectionName(collectionName);
  return collection(db, 'companies', companyId, norm);
}

// Legacy helpers
// Legacy helpers removed; new schema only

// Adapter: Fetch user settings, prefer new company-scoped settings, fallback to legacy
export async function fetchUserSettings(db, { companyId }) {
  if (!companyId) return { success: false, error: 'No companyId' };
  const ref = doc(db, 'companies', companyId, 'settings', 'default');
  const snap = await getDoc(ref);
  if (snap.exists()) return { success: true, data: { id: 'default', ...snap.data() } };
  return { success: false, error: 'Settings not found' };
}

// Adapter: Save user settings to new schema; optionally mirror to legacy during transition
export async function saveUserSettings(db, { companyId }, settingsData) {
  if (!db) throw new Error('No db');
  if (!companyId) throw new Error('No companyId');
  const ref = doc(db, 'companies', companyId, 'settings', 'default');
  await setDoc(ref, settingsData, { merge: true });
  return { success: true };
}

/** Normalize a raw Firestore company doc into flat fields for UI and bill previews. */
export function normalizeCompanyDocSnapshot(data, companyId = '') {
  if (!data || typeof data !== 'object') return {};
  const leg = data.details && typeof data.details === 'object' ? data.details : {};
  const resolvedName =
    data.name ||
    leg.firmName ||
    leg.companyName ||
    leg.businessName ||
    '';
  const signatureUrl =
    data.branding?.signatureUrl ||
    data.signatureUrl ||
    data.signUrl ||
    leg.signatureUrl ||
    leg.signUrl ||
    '';

  return {
    id: companyId || data.companyId || '',
    name: resolvedName,
    firmName: resolvedName,
    gstin:
      data.basicInfo?.gstNumber ||
      data.gstNumber ||
      leg.gstNumber ||
      leg.gstin ||
      '',
    contactNumber:
      data.basicInfo?.phone ||
      data.phone ||
      leg.phone ||
      leg.contactNumber ||
      '',
    gstinType:
      data.basicInfo?.gstType ||
      data.gstType ||
      leg.gstinType ||
      leg.gstType ||
      'Unregistered',
    companyType: data.basicInfo?.companyType || data.companyType || leg.companyType || '',
    companyTypeOther: data.basicInfo?.companyTypeOther || data.companyTypeOther || leg.companyTypeOther || '',
    businessType: data.basicInfo?.businessType || data.businessType || '',
    contactPerson: data.basicInfo?.contactPerson || data.contactPerson || '',
    email: data.basicInfo?.email || data.email || leg.email || '',
    phone: data.basicInfo?.phone || data.phone || leg.phone || leg.contactNumber || '',
    pan: data.basicInfo?.pan || data.pan || '',
    gstNumber:
      data.basicInfo?.gstNumber ||
      data.gstNumber ||
      leg.gstNumber ||
      leg.gstin ||
      '',
    gstType:
      data.basicInfo?.gstType ||
      data.gstType ||
      leg.gstinType ||
      leg.gstType ||
      '',
    currency: data.basicInfo?.currency || data.currency || 'INR',
    address: data.details?.address || leg.address || data.address || '',
    city: data.details?.city || leg.city || data.city || '',
    state: data.details?.state || leg.state || data.state || '',
    pincode: data.details?.pincode || leg.pincode || data.pincode || '',
    country: data.details?.country || leg.country || data.country || 'India',
    bankName: data.bankDetails?.bankName || data.bankName || leg.bankName || '',
    bankAccount: data.bankDetails?.bankAccount || data.bankAccount || leg.bankAccount || '',
    bankIfsc: data.bankDetails?.bankIfsc || data.bankIfsc || leg.bankIfsc || '',
    upiId: data.bankDetails?.upiId || data.upiId || leg.upiId || '',
    upiQrUrl: data.bankDetails?.upiQrUrl || data.upiQrUrl || leg.upiQrUrl || '',
    paymentGatewayLink:
      data.bankDetails?.paymentGatewayLink ||
      data.paymentGatewayLink ||
      leg.paymentGatewayLink ||
      '',
    logoUrl:
      data.branding?.logoUrl ||
      data.logoUrl ||
      leg.logoUrl ||
      leg.logo ||
      '',
    sealUrl: data.branding?.sealUrl || data.sealUrl || leg.sealUrl || '',
    signatureUrl,
    signUrl: signatureUrl,
    footer: data.termsAndConditions?.footer || data.footer || leg.footer || '',
    salesTerms: data.termsAndConditions?.salesTerms || data.salesTerms || leg.salesTerms || '',
    purchaseTerms: data.termsAndConditions?.purchaseTerms || data.purchaseTerms || leg.purchaseTerms || '',
    quotationTerms:
      data.termsAndConditions?.quotationTerms ||
      data.quotationTerms ||
      leg.quotationTerms ||
      '',
    defaultCurrency: data.preferences?.defaultCurrency || data.defaultCurrency || data.currency || 'INR',
    dateFormat: data.preferences?.dateFormat || data.dateFormat || leg.dateFormat || 'DD/MM/YYYY',
    gstCalculation: data.preferences?.gstCalculation || data.gstCalculation || 'excluding',
    selectedPlan: data.preferences?.selectedPlan || data.selectedPlan || data.plan || 'basic',
    plan: data.plan || 'basic',
    features: data.features || {},
    status: data.status || 'active',
    basicInfo: data.basicInfo || {},
    branding: data.branding || {},
    details: data.details || {},
    termsAndConditions: data.termsAndConditions || {},
  };
}

// Adapter: Fetch company details; in new schema they live in organized maps
export async function fetchCompanyDetails(db, { companyId }) {
  if (!companyId) return { success: false, error: 'No companyId' };
  const companyRef = getCompanyDocRef(db, companyId);
  const snap = await getDoc(companyRef);
  if (snap.exists()) {
    return { success: true, data: normalizeCompanyDocSnapshot(snap.data() || {}, companyId) };
  }
  return { success: false, error: 'Company details not found' };
}

// Adapter: Save company details into new organized structure
export async function saveCompanyDetails(db, { companyId }, companyDetails) {
  if (!db) throw new Error('No db');
  if (!companyId) throw new Error('No companyId');
  const ref = getCompanyDocRef(db, companyId);

  // Normalize field names from Company Details UI (firmName, gstin, contactNumber, gstinType, signUrl)
  const firmName =
    companyDetails.firmName ||
    companyDetails.name ||
    companyDetails.companyName ||
    '';
  const phone = companyDetails.phone || companyDetails.contactNumber || '';
  const gstNumber = companyDetails.gstNumber || companyDetails.gstin || '';
  const gstType = companyDetails.gstType || companyDetails.gstinType || '';
  const signatureUrl = companyDetails.signatureUrl || companyDetails.signUrl || '';

  // Organize data into the new map structure
  const payload = {
    // Root display name — used by getCompanyInfo and dashboard
    ...(firmName ? { name: firmName } : {}),
    // Basic info map
    basicInfo: {
      companyType: companyDetails.companyType || '',
      companyTypeOther: companyDetails.companyTypeOther || '',
      businessType: companyDetails.businessType || '',
      contactPerson: companyDetails.contactPerson || '',
      email: companyDetails.email || '',
      phone,
      pan: companyDetails.pan || '',
      gstNumber,
      gstType,
      currency: companyDetails.currency || 'INR'
    },
    // Details map
    details: {
      address: companyDetails.address || '',
      city: companyDetails.city || '',
      state: companyDetails.state || '',
      pincode: companyDetails.pincode || '',
      country: companyDetails.country || 'India'
    },
    // Bank details map
    bankDetails: {
      bankName: companyDetails.bankName || '',
      bankAccount: companyDetails.bankAccount || '',
      bankIfsc: companyDetails.bankIfsc || '',
      upiId: companyDetails.upiId || '',
      upiQrUrl: companyDetails.upiQrUrl || '',
      paymentGatewayLink: companyDetails.paymentGatewayLink || ''
    },
    // Branding map
    branding: {
      logoUrl: companyDetails.logoUrl || '',
      sealUrl: companyDetails.sealUrl || '',
      signatureUrl
    },
    // Terms and conditions map
    termsAndConditions: {
      footer: companyDetails.footer || '',
      salesTerms: companyDetails.salesTerms || '',
      purchaseTerms: companyDetails.purchaseTerms || '',
      quotationTerms: companyDetails.quotationTerms || ''
    },
    // Preferences map
    preferences: {
      defaultCurrency: companyDetails.defaultCurrency || companyDetails.currency || 'INR',
      dateFormat: companyDetails.dateFormat || 'DD/MM/YYYY',
      gstCalculation: companyDetails.gstCalculation || 'excluding',
      selectedPlan: companyDetails.selectedPlan || 'basic'
    },
    updatedAt: new Date()
  };
  
  await setDoc(ref, payload, { merge: true });
  return { success: true };
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
}

/** Flatten live snapshot + context into one object for bill previews and exports. */
export function buildBillPreviewCompanyDetails(company = {}, currentCompany = {}) {
  const live = company || {};
  const ctx = currentCompany || {};
  const ctxNorm =
    ctx.termsAndConditions || ctx.basicInfo || ctx.bankDetails || ctx.branding
      ? normalizeCompanyDocSnapshot(ctx)
      : ctx;
  const firmName = firstNonEmpty(live.firmName, live.name, ctxNorm.firmName, ctxNorm.name, ctx.firmName, ctx.name);
  const salesTerms = firstNonEmpty(
    live.salesTerms,
    ctxNorm.salesTerms,
    ctx.salesTerms,
    ctx.termsAndConditions?.salesTerms,
    live.terms,
    ctx.terms
  );

  return {
    name: firmName,
    firmName,
    logoUrl: firstNonEmpty(live.logoUrl, live.branding?.logoUrl, ctxNorm.logoUrl, ctx.logoUrl, ctx.branding?.logoUrl),
    address: firstNonEmpty(live.address, ctxNorm.address, ctx.address, ctx.details?.address),
    city: firstNonEmpty(live.city, ctxNorm.city, ctx.city, ctx.details?.city),
    pincode: firstNonEmpty(live.pincode, ctxNorm.pincode, ctx.pincode, ctx.details?.pincode),
    state: firstNonEmpty(live.state, ctxNorm.state, ctx.state, ctx.details?.state),
    country: firstNonEmpty(live.country, ctxNorm.country, ctx.country, 'India'),
    gstin: firstNonEmpty(live.gstin, live.gstNumber, ctxNorm.gstin, ctx.gstin, ctx.basicInfo?.gstNumber, ctx.gstNumber),
    contactNumber: firstNonEmpty(live.contactNumber, live.phone, ctxNorm.contactNumber, ctx.phone, ctx.basicInfo?.phone),
    email: firstNonEmpty(live.email, ctxNorm.email, ctx.email, ctx.basicInfo?.email),
    pan: firstNonEmpty(live.pan, ctxNorm.pan, ctx.pan, ctx.basicInfo?.pan),
    gstinType: firstNonEmpty(live.gstinType, live.gstType, ctxNorm.gstinType, ctx.gstType, ctx.basicInfo?.gstType, 'Unregistered'),
    bankName: firstNonEmpty(live.bankName, ctxNorm.bankName, ctx.bankName),
    bankAccount: firstNonEmpty(live.bankAccount, ctxNorm.bankAccount, ctx.bankAccount),
    bankIfsc: firstNonEmpty(live.bankIfsc, ctxNorm.bankIfsc, ctx.bankIfsc),
    upiId: firstNonEmpty(live.upiId, ctxNorm.upiId, ctx.upiId),
    upiQrUrl: firstNonEmpty(live.upiQrUrl, ctxNorm.upiQrUrl, ctx.upiQrUrl),
    paymentGatewayLink: firstNonEmpty(live.paymentGatewayLink, ctxNorm.paymentGatewayLink, ctx.paymentGatewayLink),
    sealUrl: resolveCompanySealUrl(live, ctx),
    signUrl: resolveCompanySignUrl(live, ctx),
    footer: firstNonEmpty(live.footer, ctxNorm.footer, ctx.footer, ctx.termsAndConditions?.footer),
    salesTerms,
    terms: salesTerms,
    purchaseTerms: firstNonEmpty(live.purchaseTerms, ctxNorm.purchaseTerms, ctx.purchaseTerms, ctx.termsAndConditions?.purchaseTerms),
    quotationTerms: firstNonEmpty(
      live.quotationTerms,
      ctxNorm.quotationTerms,
      ctx.quotationTerms,
      ctx.termsAndConditions?.quotationTerms
    ),
    dateFormat: firstNonEmpty(live.dateFormat, ctxNorm.dateFormat, ctx.dateFormat, ctx.preferences?.dateFormat, 'DD/MM/YYYY'),
  };
}

export function buildBillPreviewBankDetails(company = {}, currentCompany = {}) {
  const d = buildBillPreviewCompanyDetails(company, currentCompany);
  return {
    name: d.bankName,
    account: d.bankAccount,
    ifsc: d.bankIfsc,
    upi: d.upiId,
    upiQrUrl: d.upiQrUrl,
    paymentGatewayLink: d.paymentGatewayLink,
    sealUrl: d.sealUrl,
    signUrl: d.signUrl,
  };
}

/** Resolve company signature URL across legacy and organized Firestore shapes. */
export function resolveCompanySignUrl(...sources) {
  for (const c of sources) {
    if (!c || typeof c !== 'object') continue;
    const url =
      c.signatureUrl ||
      c.signUrl ||
      c.details?.signatureUrl ||
      c.details?.signUrl ||
      c.branding?.signatureUrl;
    if (url) return url;
  }
  return '';
}

/** Resolve company seal URL across legacy and organized Firestore shapes. */
export function resolveCompanySealUrl(...sources) {
  for (const c of sources) {
    if (!c || typeof c !== 'object') continue;
    const url = c.sealUrl || c.details?.sealUrl || c.branding?.sealUrl;
    if (url) return url;
  }
  return '';
}

// Adapter: Generic collection getter for lists (items, parties, salesBills, etc.)
// With fallback to legacy structure for backward compatibility
export function getCollectionForCompany(db, { companyId, appId, userId }, collectionName) {
  const norm = normalizeCollectionName(collectionName);
  
  if (companyId) {
    return getCompanyCollectionRef(db, companyId, norm);
  }
  
  // Fallback to legacy structure if companyId is not available
  if (appId && userId) {
    return collection(db, `artifacts/${appId}/users/${userId}/${norm}`);
  }
  return null;
}

// Employees API
export function getEmployeesCollection(db, companyId) {
  return getCompanyCollectionRef(db, companyId, 'employees');
}

export async function addOrUpdateEmployee(db, companyId, employeeId, employee) {
  const ref = doc(db, 'companies', companyId, 'employees', employeeId);
  await setDoc(ref, employee, { merge: true });
  return { success: true };
}

// Employee salary details API
export function getEmployeeSalaryDetailsCollection(db, companyId) {
  return getCompanyCollectionRef(db, companyId, 'EmployeeSalarydetails');
}

export async function addOrUpdateEmployeeSalaryDetails(db, companyId, recordId, details) {
  const ref = doc(db, 'companies', companyId, 'EmployeeSalarydetails', recordId);
  await setDoc(ref, details, { merge: true });
  return { success: true };
}

// Adapter: Update any doc with new preferred path or legacy fallback
export async function upsertDocForCompany(db, { companyId }, collectionName, docId, data) {
  const norm = normalizeCollectionName(collectionName);
  if (!db) throw new Error('No db');
  if (!companyId) throw new Error('No companyId');
  const ref = doc(db, 'companies', companyId, norm, docId);
  await setDoc(ref, data, { merge: true });
  return { success: true };
}

// Adapter: Update doc convenience
export async function updateDocForCompany(db, { companyId }, collectionName, docId, data) {
  const norm = normalizeCollectionName(collectionName);
  if (!companyId) throw new Error('No companyId');
  const ref = doc(db, 'companies', companyId, norm, docId);
  await updateDoc(ref, data);
  return { success: true };
}


